import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authenticatedFetch } from "@bastion/data";
import type {
  CreateDashboardInput,
  Dashboard,
  DashboardParameterValue,
  UpdateDashboardInput,
  WidgetDataRow,
} from "../types";
import { missingSqlBindParamNames } from "../types";

export function useDashboards() {
  return useQuery({
    queryKey: ["dashboards"],
    queryFn: () => authenticatedFetch<Dashboard[]>("/dashboards"),
  });
}

export function useDashboard(id: string | null) {
  return useQuery({
    queryKey: ["dashboards", id],
    queryFn: () => authenticatedFetch<Dashboard>(`/dashboards/${id}`),
    enabled: !!id,
  });
}

export function useWidgetData(sql: string | null, params?: Record<string, DashboardParameterValue>) {
  const hasAllParams = sql ? missingSqlBindParamNames(sql, params).length === 0 : false;
  return useQuery({
    queryKey: ["dashboard-widget-data", sql, params ?? {}],
    queryFn: () =>
      authenticatedFetch<{ columns: string[]; rows: WidgetDataRow[] }>("/sql-query", {
        method: "POST",
        body: JSON.stringify({ sql, params: params ?? {} }),
      }),
    // Don't fire while dashboard parameters are still being initialized.
    // Otherwise widgets with SQL like `... between :date_range_start and
    // :date_range_end` can briefly POST with `{}` during first render,
    // producing noisy 400/422s before the real resolved params arrive.
    enabled: !!sql && hasAllParams,
    staleTime: 30_000,
  });
}

export function useCreateDashboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDashboardInput) =>
      authenticatedFetch<Dashboard>("/dashboards", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dashboards"] });
    },
  });
}

export function useUpdateDashboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...updates }: UpdateDashboardInput) =>
      authenticatedFetch<Dashboard>(`/dashboards/${id}`, {
        method: "PATCH",
        body: JSON.stringify(updates),
      }),
    onSuccess: (dashboard) => {
      queryClient.invalidateQueries({ queryKey: ["dashboards"] });
      queryClient.invalidateQueries({ queryKey: ["dashboards", dashboard.id] });
    },
  });
}

export function useDeleteDashboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      authenticatedFetch<void>(`/dashboards/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dashboards"] });
    },
  });
}

export function useDuplicateDashboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      authenticatedFetch<Dashboard>(`/dashboards/${id}/duplicate`, {
        method: "POST",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dashboards"] });
    },
  });
}
